import React, { useEffect, useState, useRef } from "react";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import ThemeOverrideHandler from "../components/ThemeOverrideHandler";
// This imports subscription verification helpers
import {
  verifyCheckoutSession,
  verifySubscriptionPayment,
} from "../api/apiFunction/paymentServices";
// This imports trial verification for Tap 3DS redirects
import { verifyTrial } from "../api/apiFunction/userServices";

const PaymentVerify = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    // This verifies either subscription payments or trial authorizations
    const verifyPayment = async () => {
      // Prevent double API calls in StrictMode
      if (hasVerifiedRef.current) return;
      hasVerifiedRef.current = true;
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const pathname = window.location.pathname || "";

        // Check for different URL parameter formats
        const tapId = urlParams.get("tap_id");
        const stripeId =
          urlParams.get("stripe_id") || urlParams.get("session_id");
        const provider = urlParams.get("provider");

        let chargeId = null;
        let detectedProvider = null;

        // Determine charge ID and provider
        if (tapId) {
          chargeId = tapId;
          detectedProvider = "tap";
        } else if (stripeId) {
          chargeId = stripeId;
          detectedProvider = "stripe";
        } else {
          setStatus("error");
          setMessage("Missing payment ID. Please try the payment again.");
          return;
        }

        // Set payment details for display
        setPaymentDetails({
          sessionId: chargeId,
          provider: detectedProvider,
        });

        let res;
        const isTrialPath = pathname.includes("/trial/verify");

        // If this path is trial verification, use user verify-trial endpoint
        if (isTrialPath) {
          setIsTrial(true);
          res = await verifyTrial(chargeId);
        } else {
          // Otherwise, verify subscription payment (Tap/Stripe)
          try {
            res = await verifySubscriptionPayment(
              chargeId,
              provider || detectedProvider
            );
          } catch (newApiError) {
            if (detectedProvider === "tap") {
              res = await verifyCheckoutSession(chargeId);
            } else {
              throw newApiError;
            }
          }
        }

        if (res.data && (res.data.success || res.data.user)) {
          setStatus("success");

          // Update user info in localStorage if present
          const user = localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))
            : null;
          if (user && res.data.user) {
            // Save upgraded plan and trial status in localStorage
            user.plan = res.data.user.plan;
            user.trial = res.data.user.trial || user.trial;
            setUserRole(res.data.user.role);
            localStorage.setItem("user", JSON.stringify(user));

            // Set user details for display (both trial and subscription)
            setUserDetails({
              name: user.name,
              email: user.email,
              trial: user.trial,
              plan: user.plan,
            });
          }

          // Clean up stored payment data
          localStorage.removeItem("payment_session_id");
          localStorage.removeItem("thawani_session_id"); // Legacy cleanup
        } else {
          setStatus("error");
          setMessage(res.data?.message || "Payment not completed.");
        }
      } catch (err) {
        setStatus("error");
        setMessage(
          "Failed to verify payment. Please contact support if this persists."
        );
      }
    };

    verifyPayment();
  }, []);

  const handleGoToDashboard = () => {
    if (userRole === "admin") {
      window.location.href = "/org/dashboard";
    } else {
      window.location.href = "/chat";
    }
  };

  const handleRetryPayment = () => {
    window.location.href = "/upgrade-plan";
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <>
      <ThemeOverrideHandler theme="light" />
      <div className="min-h-screen flex flex-col items-center justify-center bg-bg-50 px-4">
        <div className="max-w-md w-full text-center">
          {/* Status Icon */}
          <div className="mb-6">
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
                status === "loading"
                  ? "bg-blue-100 dark:bg-blue-900"
                  : status === "success"
                  ? "bg-green-100 dark:bg-green-900"
                  : "bg-red-100 dark:bg-red-900"
              }`}
            >
              {status === "loading" && (
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
              )}
              {status === "success" && (
                <CheckCircle className="w-8 h-8 text-green-600" />
              )}
              {status === "error" && (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
          </div>

          {/* Title */}
          <h1
            className={`text-3xl font-bold mb-4 ${
              status === "loading"
                ? "text-fg-50"
                : status === "success"
                ? "text-fg-50"
                : "text-fg-50"
            }`}
          >
            {status === "loading" && "Verifying Payment"}
            {status === "success" &&
              (isTrial ? "Free Trial Activated!" : "Payment Successful!")}
            {status === "error" && "Payment Unsuccessful"}
          </h1>

          {/* Message */}
          <p className="text-fg-60 text-lg mb-8">
            {status === "loading" &&
              "Please wait while we verify your payment..."}
            {status === "success" &&
              (isTrial
                ? "Enjoy 7 days of full access to your new features. 🚀"
                : "Your plan has been upgraded. Enjoy your new features! 🚀")}
            {status === "error" &&
              (message ||
                "Something went wrong. Please try again or contact support.")}
          </p>

          {/* Details Section - Show User Details from localStorage */}
          {status !== "loading" && userDetails && (
            <div className="bg-bg-60 rounded-lg p-4 mb-8 text-left">
              <h3 className="font-medium text-fg-50 mb-3">Account Details</h3>
              <div className="space-y-2 text-sm text-fg-60">
                <div>
                  <span className="font-medium">Name:</span> {userDetails.name}
                </div>
                <div>
                  <span className="font-medium">Email:</span>{" "}
                  {userDetails.email}
                </div>
                {userDetails.plan && (
                  <div>
                    <span className="font-medium">Plan:</span>{" "}
                    {userDetails.plan.name
                      ? userDetails.plan.name.charAt(0).toUpperCase() +
                        userDetails.plan.name.slice(1)
                      : "N/A"}
                  </div>
                )}
                {isTrial && userDetails.trial && (
                  <>
                    <div>
                      <span className="font-medium">Trial Status:</span>{" "}
                      {userDetails.trial.status
                        ? userDetails.trial.status.charAt(0).toUpperCase() +
                          userDetails.trial.status.slice(1)
                        : "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Days Remaining:</span>{" "}
                      {userDetails.trial.daysRemaining || 7}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {status === "success" && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleGoHome}
                className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
              >
                Back to Home
              </button>
              <button
                onClick={handleGoToDashboard}
                className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
              >
                {userRole === "admin" ? "Go to Dashboard" : "Go to Chat"}
              </button>
            </div>
          )}

          {status === "error" && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleGoHome}
                className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
              >
                Back to Home
              </button>
              <button
                onClick={handleRetryPayment}
                className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Help Text */}
          {status !== "loading" && (
            <div className="mt-8 text-sm text-fg-60">
              <p>
                Need help? Contact our{" "}
                <a
                  href="/contact"
                  className="text-ac-02 hover:text-ac-01 underline"
                >
                  support team
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentVerify;
